import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { BRAND_CLASSES } from '@/constants/theme';
/**
 * RadioGroup component for single-select radio button groups.
 * Works with react-hook-form via Controller.
 *
 * @example
 * // Standalone usage
 * const [role, setRole] = useState<string | null>(null)
 *
 * <RadioGroup
 *   label="Role"
 *   name="role"
 *   options={[
 *     { value: 'admin', label: 'Admin' },
 *     { value: 'editor', label: 'Editor' },
 *   ]}
 *   value={role}
 *   onChange={setRole}
 *   allowDeselect
 * />
 *
 * @example
 * // With react-hook-form Controller
 * <Controller
 *   name="role"
 *   control={control}
 *   rules={{ required: 'Role is required' }}
 *   render={({ field, fieldState }) => (
 *     <RadioGroup
 *       label="Role"
 *       name="role"
 *       options={roleOptions}
 *       value={field.value}
 *       onChange={field.onChange}
 *       error={fieldState.error?.message}
 *     />
 *   )}
 * />
 */
export function RadioGroup({ label, required, name, error, helperText, options, value, onChange, containerClassName = '', allowDeselect = false, layout = 'vertical' }) {
    return (_jsxs("div", { className: containerClassName, children: [label && (_jsxs("label", { className: `block text-sm font-medium mb-2 ${BRAND_CLASSES.INPUT.LABEL}`, children: [label, required && _jsx("span", { className: `${BRAND_CLASSES.INPUT.REQUIRED} ml-1`, children: "*" })] })), _jsx("div", { className: layout === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 gap-3' : 'space-y-2', children: options.map(option => {
                    const isSelected = value === option.value;
                    return (_jsxs("label", { className: `flex items-start space-x-2 ${option.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`, onClick: () => {
                            // Handle deselection: clicking an already-selected radio deselects it
                            if (allowDeselect && isSelected && !option.disabled) {
                                onChange?.(null);
                            }
                        }, children: [_jsx("input", { type: "radio", name: name, checked: isSelected, disabled: option.disabled, onChange: () => {
                                    if (!option.disabled) {
                                        onChange?.(option.value);
                                    }
                                }, className: "mt-0.5 border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 disabled:opacity-50" }), _jsxs("div", { children: [_jsx("span", { className: `text-sm ${option.disabled ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-300'}`, children: option.label }), option.description && (_jsx("p", { className: `text-xs ${BRAND_CLASSES.INPUT.HELPER}`, children: option.description }))] })] }, option.value));
                }) }), error && (_jsx("p", { className: `mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`, children: error })), !error && helperText && (_jsx("p", { className: `mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`, children: helperText }))] }));
}
